import type { Engagement } from './engagements';

export enum UserRole {
  Lead = 'lead',
  Admin = 'admin',
  Edit = 'edit',
  View = 'view',
}

export enum UserStatus {
  Active = 'active',
  Unconfirmed = 'unconfirmed',
  Inactive = 'inactive',
}

export enum UserModePermission {
  Supplier = 'supplier',
  Client = 'client',
  ClientSupplier = 'client_supplier',
  Federated = 'federated',
}

export enum MFAPreference {
  SMS = 'SMS',
  TOTP = 'TOTP',
  DISABLED = 'DISABLED',
}

// https://github.com/riskledger/rl/blob/dev/services/monolith/pkg/models/user.go#L48-L73
export type User = {
  userID: string;
  cognitoID: string;
  organisationID: string;
  email: string; // This is optional on the backend for deleted users, but these users aren't sent to the frontend
  firstName: string;
  lastName: string;
  phone: string;
  jobTitle: string;
  role: UserRole;
  roleID: string;
  language: string;
  createdAt: string;
  activatedAt?: string;
  modifiedAt?: string;
  marketingEmails: boolean;
  deleted: boolean;
  deletedBy?: string;
  deletedAt?: string;
  deletedReason: string;
  version: number;
  status: UserStatus;
  team: string;
  modePermission: UserModePermission;
  isLead: boolean;
  // Always present on the wire (*string, no omitempty), null when unenrolled.
  mfaPreference: MFAPreference | null;
  restrictSupplierAccess: boolean;
  linkedinLink: string;
  profilePicture: string;
  profileComplete: boolean;
  engagements?: Engagement[];
};

export type UserWithCognitoStatus = User & {
  cognitoStatus: string;
};

type LoginActivity = {
  hasLoggedIn: boolean;
  lastLoggedIn?: string;
};

export type ZeusActiveUser = User & {
  loginActivity: LoginActivity;
};

export type UserPatch = {
  firstName: string;
  lastName: string;
  jobTitle?: string;
  phone: string;
  language: string;
  marketingEmails?: boolean;
  role: UserRole;
  roleID: string;
  team: string;
  modePermission: UserModePermission;
  isLead: boolean;
  restrictSupplierAccess: boolean;
  linkedinLink: string | null;
  profileComplete: boolean;
  profilePicture: string | null;
};

// `removed`: membership soft-deleted but the user account still exists. Absent
// on older backends; treated as `active`.
export type MembershipState = 'active' | 'removed';

// https://github.com/riskledger/rl/blob/dev/services/monolith/pkg/api/user.go#L120-L125
export type UserBasicInfo = {
  id: string;
  fullName: string;
  /** @deprecated */
  role: string;
  roleId: string;
  isLead: boolean;
  /** @deprecated */
  modePermission: string;
  email?: string;
  team: string;
  restrictSupplierAccess: boolean;
  assignedSuppliers: string[];
  membershipState?: MembershipState;
};
